// Web adapter for the mobile crypto bundle's `storage` import.
// Contract (per cl-readme): secureGet(key, fallback) / secureSet(key, value) with JSON-ish
// string values (private identity keys are base64 strings inside a JSON object).
//
// Hardening (F1 pentest #1): values are encrypted at rest with a NON-EXTRACTABLE
// AES-GCM key held as a CryptoKey inside IndexedDB. Because the key is non-extractable,
// its raw bytes can never be read out by script (even via XSS), so E2EE private keys
// cannot be exfiltrated from the browser store. `wipeCryptoStorage()` drops the whole DB
// on logout so keys never linger on a shared device.
const DB_NAME = "bloq_cipherlock";
const STORE = "kv";
const WRAP_KEY_ID = "__wrapkey_v1__";
let dbPromise = null;
let wrapKeyPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function idbGet(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, "readonly").objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbSet(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Get-or-create the non-extractable AES-GCM wrapping key (stored as a CryptoKey object).
function getWrapKey() {
  if (wrapKeyPromise) return wrapKeyPromise;
  wrapKeyPromise = (async () => {
    const existing = await idbGet(WRAP_KEY_ID);
    if (existing && existing.type === "secret") return existing; // CryptoKey survives structured clone
    const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
    await idbSet(WRAP_KEY_ID, key);
    return key;
  })();
  return wrapKeyPromise;
}

const enc = new TextEncoder();
const dec = new TextDecoder();

async function encryptString(str) {
  const key = await getWrapKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, enc.encode(str));
  return { __enc: 1, iv, ct }; // iv:Uint8Array, ct:ArrayBuffer — both structured-clonable
}

async function decryptRecord(rec) {
  const key = await getWrapKey();
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: rec.iv }, key, rec.ct);
  return dec.decode(pt);
}

export const storage = {
  async secureGet(key, fallback = null) {
    try {
      const v = await idbGet(key);
      if (v === undefined || v === null) return fallback;
      if (v && typeof v === "object" && v.__enc === 1) return await decryptRecord(v);
      return v; // legacy plaintext value (migrated on next write)
    } catch {
      return fallback;
    }
  },
  async secureSet(key, value) {
    try {
      await idbSet(key, await encryptString(String(value)));
    } catch {
      /* crypto/storage unavailable — fail closed (do not silently store plaintext) */
    }
  },
};

// Purge all Cipher Lock material (identity, session + group keys, wrap key) from the device.
// Called on logout so nothing lingers on a shared browser.
export function wipeCryptoStorage() {
  dbPromise = null;
  wrapKeyPromise = null;
  return new Promise((resolve) => {
    try {
      const req = indexedDB.deleteDatabase(DB_NAME);
      req.onsuccess = req.onerror = req.onblocked = () => resolve();
    } catch {
      resolve();
    }
  });
}

export default storage;
